/* Verbatim port of coreAsset, influenceScore, footprint, keyDomains,
 * threatItems from service/review/index.html (~950-971, ~1088-1099,
 * ~1152-1220).
 *
 * PARAMETERIZED (rule a): every function here read the global `STATE`
 * (or, for threatItems, `STATE` + `assetForUrl` + `canEdit`) — each now
 * takes `state: AppPayloadState` as an explicit first parameter.
 *
 * DEVIATION: `assetForUrl` (originally ~1137, between footprint and
 * keyDomains) is placed here rather than in derive/actions.ts as the F1
 * brief's initial module map suggested. Reason: threatItems() (this file)
 * calls assetForUrl() directly, and derive/actions.ts's actionPlan() calls
 * threatItems() — putting assetForUrl in actions.ts would create an
 * actions.ts <-> exec.ts import cycle. Co-locating it with its one
 * derivation caller avoids that; derive/actions.ts imports assetForUrl
 * from here instead. Documented per the brief's "moving it between these
 * modules is fine — document it" allowance. Verbatim body either way.
 */
import { domainOf, fixMojibake } from '../lib/format.ts';
import { BAD_STATUS, canEdit } from './axes.ts';
import type { AppPayloadState } from './state-shape.ts';
import type { NormalizedAsset, RawAssetFields } from '../types/contract.ts';

/** One row of keyDomains(): urlDurability aggregated per domain. */
export interface KeyDomain {
  domain: string;
  polls: number;
  best: number;
  urls: number;
  share: number;
}

/** One threat row (search plane or AI plane) — feeds actionPlan + findings. */
export interface ThreatItem {
  score: number;
  sev: 'high' | 'medium' | 'low';
  title: string | null;
  url: string;
  domain: string | null;
  why: string;
  action: string;
  kind: 'search' | 'ai';
}

/* `undefined` when the run has no assets at all (honest — callers already
 * guard with `core && …`); the original's `: NormalizedAsset` hid that. */
export function coreAsset(state: AppPayloadState): NormalizedAsset | undefined {
  return state.assets.find((a) => a.isCoreEntity) || state.assets[0];
}

/* Reputation influence per asset, 0..1 — how much this surface shapes what a
   searcher or an AI engine actually sees. Built from MEASURED planes when
   present (IMPACT mean position, AI citations, captured-SERP rank), with a
   light structural fallback (role, followers) so pre-enrichment payloads
   still render sensibly. Canonical: node sizing and any future "top surfaces"
   list must both read this. */
export function influenceScore(state: AppPayloadState, a: NormalizedAsset): number {
  const f: RawAssetFields = a.fields || {};
  let s = 0;
  if (f.aiCitations30d) s += Math.min(1, Math.log10(1 + +f.aiCitations30d) / 3) * .45;
  if (f.impactMeanPosition != null)
    s += Math.max(0, (21 - Math.min(+f.impactMeanPosition, 21)) / 20) * .45;
  const hit = state.serp && a.url && state.serp.results.find((r) =>
    r.page === 1 && domainOf(r.url) === domainOf(a.url));
  if (hit) s += ((11 - hit.rank) / 10) * .3;
  // String(...) is the same coercion the untyped parseInt(f.followers) did
  const foll = parseInt(String(f.followers));
  if (foll) s += Math.min(1, Math.log10(foll) / 6) * .15;
  if (a.role === 'hub') s += .08;
  return Math.min(1, s);
}

/* Footprint numbers (exec + §4 share these) */
export function footprint(state: AppPayloadState) {
  const A = state.assets;
  const controlled = A.filter(a => a.category === 'owned' || canEdit(a)).length;
  const claimable = A.filter(a => a.category === 'claimable').length;
  const atRisk = A.filter(a => BAD_STATUS.includes(a.status)).length;
  const review = A.filter((a) => a.needsReview).length;
  return { total: A.length, controlled, claimable, atRisk, review,
           owned: A.filter(a=>a.category==='owned').length,
           editable: A.filter(a=>a.category==='editable').length,
           media: A.filter(a=>a.category==='media').length };
}

/* asset lookup for a URL/domain: exact URL first; else the domain's
   MAJORITY-category representative (one odd asset on a shared domain — e.g.
   a defunct brand's Wikipedia page — must not misidentify the domain) */
export function assetForUrl(state: AppPayloadState, url: string | null | undefined): NormalizedAsset | null {
  if (!url) return null;
  const clean = url.split('#')[0];
  const exact = state.assets.find((a) => a.url && a.url.split('#')[0] === clean);
  if (exact) return exact;
  const d = domainOf(url);
  if (!d) return null;
  const matches = state.assets.filter((a) => a.url && domainOf(a.url) === d);
  if (!matches.length) return null;
  const counts: Record<string, number> = {};
  matches.forEach((a) => counts[a.category] = (counts[a.category] || 0) + 1);
  const cat = Object.keys(counts).sort((a,b) => (counts[b] || 0) - (counts[a] || 0))[0];
  return matches.find((a) => a.category === cat) || matches[0] || null;
}

/* "Key domains in search": urlDurability aggregated by domain. The raw
   `polls` figure counts every appearance across ALL tracked searches × cities
   × polling days, so it runs into the thousands — we show each domain's
   SHARE of that total instead, which is comparable and readable. */
export function keyDomains(state: AppPayloadState, limit?: number): KeyDomain[] {
  const ud = state.impact && state.impact.urlDurability;
  if (!ud) return [];
  const byDom: Record<string, KeyDomain> = {};
  let total = 0;
  Object.entries(ud).forEach(([url, o]) => {
    const d = domainOf(url); if (!d) return;
    const e = byDom[d] = byDom[d] || { domain:d, polls:0, best:1e9, urls:0, share:0 };
    e.polls += o.polls || 0; total += o.polls || 0; e.urls++;
    if (o.meanPosition != null && o.meanPosition < e.best) e.best = o.meanPosition;
  });
  const out = Object.values(byDom).sort((a,b) => b.polls - a.polls);
  out.forEach((e) => e.share = total ? e.polls / total * 100 : 0);
  return out.slice(0, limit || 12);
}

/* Threat scoring (salvaged v3 logic; feeds the action plan + §1/§3 findings) */
export function threatItems(state: AppPayloadState): ThreatItem[] {
  const out: ThreatItem[] = []; const seen = new Set<string | null>();
  if (state.serp) state.serp.results.filter((r) => r.page === 1).forEach((r) => {
    const asset = assetForUrl(state, r.url);
    const uncontrolled = !asset || (asset.category !== 'owned' && !canEdit(asset));
    const negative = r.rating === 'negative' || (asset && asset.sentiment === 'negative');
    if (!(negative || (uncontrolled && r.rank <= 5))) return;
    const dur = asset && asset.fields && asset.fields.impactDurability;
    const score = (11 - Math.min(r.rank,10)) * (negative?3:1) * (dur==='sticky'?1.6:1) * (uncontrolled?1.4:1);
    const key = domainOf(r.url); if (seen.has(key)) return; seen.add(key);
    const isWiki = /wikipedia\.org/.test(r.url||'');
    const action = isWiki
      ? 'File a COI-compliant Talk-page edit request for factual errors, and build sticky Desirable assets to compete for the slot.'
      : negative ? 'Engage the platform (respond / request correction) and elevate Owned and Desirable results above it.'
      : 'Claim or build a client-controlled surface to compete for this page-1 slot.';
    out.push({ score, sev: negative && r.rank<=5 ? 'high' : negative ? 'medium' : 'low',
      title: fixMojibake(r.title) || domainOf(r.url), url: r.url, domain: domainOf(r.url),
      why: `Ranks #${r.rank} on the branded search${negative?' and is Undesirable':''}${uncontrolled?'; the client cannot edit it':''}.`,
      action, kind:'search' });
  });
  if (state.aiNarrative) (state.aiNarrative.citedDomains||[]).forEach((d) => {
    const delta = d.count - (d.compareCount ?? d.count);
    const mapped = (d.mappedAssetIds||[]).map((id) => state.assets.find((a) => a.id===id)).filter((a): a is NormalizedAsset => !!a);
    const controlled = mapped.some((a) => a.category==='owned' || canEdit(a));
    if (controlled || delta < 15) return;
    if (seen.has(d.domain)) return; seen.add(d.domain);
    const dom = d.domain.toLowerCase();
    let move;
    if (d.label==='Content mill' || /matrixbcg|portersfiveforce|pestle|swot/.test(dom))
      move = 'Low-authority SEO site with templated analysis content: publish authoritative owned content (IR, newsroom) on the same topics so engines cite the client instead; request correction or de-indexing if inaccurate.';
    else if (/trustpilot|tripadvisor|skytrax|airlinequality|glassdoor|yelp/.test(dom))
      move = 'Review platform: claim the profile, respond to reviews, and invite all customers to share feedback — engines are citing customer sentiment here, so genuinely improving it shifts the narrative.';
    else if (/reuters|jpost|timesofisrael|globes|bloomberg|yahoo|forbes|news/.test(dom))
      move = 'Earned media: pitch corrective or positive coverage to the same outlets, and make the owned newsroom rank for the same stories.';
    else if (/simplywall|investing|finance|stockanalysis|tradingview/.test(dom))
      move = 'Financial-data site: keep the IR site and filings complete and current so engines pull financial facts from the owned source.';
    else if (/wikipedia|wikidata/.test(dom))
      move = 'Pursue via the COI-compliant Wikipedia/Wikidata workstream — accuracy here propagates to every engine.';
    else
      move = 'Assess the source’s authority, then publish owned content on the themes it drives.';
    out.push({ score: delta * (d.label==='Content mill'?1.5:1), sev: delta>40?'high':'medium',
      title: d.domain, url:'https://'+d.domain, domain: d.domain,
      why: `Feeds the AI narrative with ${d.count} citations this month (up ${delta}) across ${d.models||'?'} engines, and the client does not control it.`,
      action: move, kind:'ai' });
  });
  out.sort((a,b) => b.score - a.score);
  return out;
}
